'use client';

// Formulario de inicio de sesión del administrador de plataforma (HU-044, CU-022): correo,
// contraseña y, si la cuenta ya tiene 2FA, el código TOTP. La sesión la abre la api
// (POST /v1/platform/auth/login, mismo origen por el rewrite /v1/*) con una cookie propia,
// HttpOnly — nunca la misma que apps/web-admin ni la de ningún tenant.
import { useRouter } from 'next/navigation';
import { type FormEvent, useState } from 'react';
import { platformLoginSchema, type PlatformLoginField } from '@vendedor/shared';
import { Button } from '@vendedor/ui';
import { sendJson } from '@/lib/api-client';
import { interpretLoginResponse } from '@/lib/login-form';
import { TextField } from './text-field';

type FieldErrors = Partial<Record<PlatformLoginField, string>>;

/** Clave de sessionStorage del URI otpauth mientras se confirma el alta del 2FA (CA-01). */
export const TOTP_SETUP_STORAGE_KEY = 'vendedor-platform-totp-setup';

export function LoginForm() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setFormError(null);

    const parsed = platformLoginSchema.safeParse({ email, password });
    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as PlatformLoginField | undefined;
        if (field && !fieldErrors[field]) fieldErrors[field] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setSubmitting(true);
    try {
      const { status, body } = await sendJson('POST', '/v1/platform/auth/login', {
        ...parsed.data,
        ...(needsTotp && totpCode ? { totpCode } : {}),
      });
      const outcome = interpretLoginResponse(status, body);
      if (outcome.kind === 'success') {
        router.replace('/panel');
        router.refresh();
        return;
      }
      if (outcome.kind === 'totp_setup_required') {
        window.sessionStorage.setItem(
          TOTP_SETUP_STORAGE_KEY,
          JSON.stringify({ adminId: outcome.adminId, otpauthUri: outcome.otpauthUri }),
        );
        router.push('/entrar/totp');
        return;
      }
      if (outcome.kind === 'totp_required') {
        setNeedsTotp(true);
      } else {
        setFormError(outcome.message);
        setTotpCode('');
      }
    } catch {
      setFormError('No hay conexión con el servidor. Revisa tu red e inténtalo de nuevo.');
    }
    setSubmitting(false);
  }

  return (
    <form
      method="post"
      noValidate
      onSubmit={onSubmit}
      className="flex flex-col gap-4"
      aria-label="Iniciar sesión"
    >
      <TextField
        label="Correo"
        name="email"
        type="email"
        inputMode="email"
        autoComplete="username"
        autoCapitalize="none"
        spellCheck={false}
        value={email}
        error={errors.email}
        onChange={(e) => {
          setEmail(e.target.value);
          setErrors((current) => ({ ...current, email: undefined }));
          setFormError(null);
        }}
      />
      <TextField
        label="Contraseña"
        name="password"
        type="password"
        autoComplete="current-password"
        value={password}
        error={errors.password}
        onChange={(e) => {
          setPassword(e.target.value);
          setErrors((current) => ({ ...current, password: undefined }));
          setFormError(null);
        }}
      />
      {needsTotp ? (
        <TextField
          label="Código de 6 dígitos"
          name="totpCode"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={totpCode}
          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
        />
      ) : null}

      {formError ? (
        <p
          className="m-0 rounded-md bg-danger-soft px-3.5 py-2.5 text-sm font-bold text-danger-ink"
          role="alert"
        >
          {formError}
        </p>
      ) : null}

      <Button type="submit" shape="field" size="action" fullWidth disabled={submitting}>
        {submitting ? 'Entrando…' : needsTotp ? 'Confirmar código' : 'Entrar'}
      </Button>
    </form>
  );
}
